import {
  RecursivePartial,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import cloneDeep from "lodash/cloneDeep";

const isEmptyObjectOrArray = (value: any): boolean => {
  if (value !== null && typeof value === "object") {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return Object.keys(value).length === 0;
    }
  }
  return false;
};

// Allow createdAt to be passed through as this is used to determine the default year that the site
// becomes operational
const defaultSiteIgnoreKeys = new Set<string>(["createdBy", "updatedAt"]);

export const buildSiteCalculationPayload = ({
  site,
  clearNonCalcFields,
}: {
  site: RecursivePartial<Site>;
  clearNonCalcFields: boolean;
}): RecursivePartial<Site> => {
  const siteCopy = cloneDeep(site);
  const siteClean = Object.fromEntries(
    Object.entries(siteCopy).filter(
      ([key, value]) =>
        value !== null &&
        value !== "" &&
        !defaultSiteIgnoreKeys.has(key) &&
        !isEmptyObjectOrArray(value)
    )
  );

  const sitePayload: Partial<Site> = { ...siteClean };
  if (clearNonCalcFields) {
    // These values don't impact calculations
    delete sitePayload.name;
    delete sitePayload.address;
    delete sitePayload.score;
    delete sitePayload.notes;
    delete sitePayload.lockNote;
    delete sitePayload.invoiceProps;
    delete sitePayload.operationalStatus;
  }
  // These values are calculation results and should not be sent to the API
  for (const [_, demand] of Object.entries(sitePayload.demand || {})) {
    if (demand) {
      delete demand.halfHourlyDemand;
    }
  }
  for (const [_, generation] of Object.entries(sitePayload.generation || {})) {
    if (generation) {
      delete generation.halfHourlyGeneration;
    }
  }
  for (const [_, esys] of Object.entries(sitePayload.esys || {})) {
    if (esys) {
      delete esys.energyFlowAnnual;
      delete esys.energyFlowMonthly;
    }
  }
  delete sitePayload.dno;
  delete sitePayload.substationData;
  delete sitePayload.energyFlowMonthly;
  delete sitePayload.energyFlowAnnual;
  delete sitePayload.energyFlowSummerDay;
  delete sitePayload.energyFlowWinterDay;
  delete sitePayload.projectCosts;
  delete sitePayload.financialModels;
  delete sitePayload.financialModelsInrange;
  delete sitePayload.energyFinancialsMonthly;
  delete sitePayload.activeInvestmentModel;
  return sitePayload;
};
